import { entityConfigs } from '@ast-data/shared';
import { withTimeout } from '../utils/withTimeout';

/**
 * Sweeping up after schema migrations.
 *
 * RxDB's Dexie storage keeps the schema version in the DATABASE NAME
 * (`rxdb-dexie-<db>--<version>--<collection>`), so every migration creates a
 * new database and leaves the old one behind: `remove()` there is
 * `dexieTable.clear()` plus a close, never a `deleteDatabase`. Nothing is lost
 * that way, but a device collects one dead database per collection per
 * migration, for ever. Neither RxDB's docs nor its API offer to tidy that up,
 * so we do it.
 *
 * It matters beyond tidiness: on an iPhone every `indexedDB.databases()` opens
 * EVERY database file of the origin, and the start-up asks that question
 * (db/openProfile.ts). Each dead database makes each start slower.
 *
 * Three rules keep this from ever costing anybody their work:
 *
 * 1. Only the copy this tab has OPEN is touched — named by the open database,
 *    not by the generation in localStorage, which another tab may have moved
 *    on. A parked generation (see openStall.ts) is never touched: it can hold
 *    the only copy of an unsent record whose journal entry the self-heal has
 *    already struck off, and it is exactly what the rescue reads.
 * 2. Nothing at all while work is waiting to be sent, and an old COLLECTION
 *    store only when provably EMPTY. An interrupted migration leaves documents
 *    in the old version and RxDB picks them up on the next start; counting
 *    first means this sweep can never be the reason they are gone.
 * 3. Bookkeeping (replication checkpoints, migration status) goes without
 *    counting: it holds no records, and a lost checkpoint costs one full pull.
 */

/** How long one database gets — for the listing, the count and the delete. */
const STEP_TIMEOUT_MS = 3000;

/** `rxdb-dexie-<db>--<version>--<rest>`, where rest is a collection or a meta store. */
const RAW_NAME = /^rxdb-dexie-(?<database>.+?)--(?<version>\d+)--(?<rest>.+)$/;

/** Collection name → the schema version this build stores it under. */
export function currentVersions(): Map<string, number> {
    const versions = new Map<string, number>();
    for (const config of entityConfigs) {
        const schema = config.rxSchema as { version?: number } | undefined;
        if (typeof schema?.version === 'number') {
            versions.set(config.collection, schema.version);
        }
    }
    return versions;
}

/**
 * RxDB's own bookkeeping beside each collection: the replication checkpoint
 * store, and the status of a migration while it runs. Neither names a
 * collection we could look up — one carries a hash, the other the version it
 * migrated FROM — so both are judged by the only thing they do say.
 */
const HOUSEKEEPING = /^(rx-replication-meta-|rx-migration-state-meta-)/;
const CHECKPOINTS = /^rx-replication-meta-/;

/**
 * The databases this build has outgrown, out of everything the browser holds.
 *
 * A collection store counts when its version is below the one in use. A
 * housekeeping store counts when its version is one no collection uses any
 * more: it then belongs to nobody.
 */
export function outdatedStoreNames(
    names: readonly string[],
    versions: Map<string, number>,
    databaseName: string,
    /**
     * The checkpoint stores this build's replications use, when known. Then a
     * checkpoint store goes as soon as it is not one of them — the version
     * rule alone keeps every old one whose version some OTHER collection
     * happens to use.
     */
    liveCheckpoints?: ReadonlySet<string>
): string[] {
    const liveVersions = new Set(versions.values());
    const outdated: string[] = [];
    for (const name of names) {
        const parts = RAW_NAME.exec(name)?.groups;
        if (!parts || parts.database !== databaseName) {
            continue;
        }
        const version = Number(parts.version);
        if (HOUSEKEEPING.test(parts.rest)) {
            const unused =
                liveCheckpoints !== undefined &&
                liveCheckpoints.size > 0 &&
                CHECKPOINTS.test(parts.rest) &&
                !liveCheckpoints.has(name);
            if (unused || !liveVersions.has(version)) {
                outdated.push(name);
            }
            continue;
        }
        const inUse = versions.get(parts.rest);
        if (inUse !== undefined && version < inUse) {
            outdated.push(name);
        }
    }
    return outdated;
}

/** How many documents a raw store holds; `null` when it would not say. */
function countDocs(name: string): Promise<number | null> {
    return withTimeout(
        new Promise<number | null>((resolve) => {
            // No version: opening at the stored one, so this can never trigger
            // an upgrade on a database somebody else may still need.
            const request = indexedDB.open(name);
            request.onsuccess = () => {
                const database = request.result;
                if (![...database.objectStoreNames].includes('docs')) {
                    database.close();
                    resolve(0);
                    return;
                }
                const count = database
                    .transaction('docs', 'readonly')
                    .objectStore('docs')
                    .count();
                count.onsuccess = () => {
                    const total = count.result;
                    database.close();
                    resolve(total);
                };
                count.onerror = () => {
                    database.close();
                    resolve(null);
                };
            };
            request.onerror = () => resolve(null);
            request.onblocked = () => resolve(null);
            // It does not exist at this version — nothing to count, and nothing
            // may be created here.
            request.onupgradeneeded = () => {
                request.transaction?.abort();
                resolve(null);
            };
        }),
        STEP_TIMEOUT_MS,
        null
    );
}

function deleteDatabase(name: string): Promise<boolean> {
    return withTimeout(
        new Promise<boolean>((resolve) => {
            const request = indexedDB.deleteDatabase(name);
            request.onsuccess = () => resolve(true);
            request.onerror = () => resolve(false);
            // Somebody still has it open. Next start, then.
            request.onblocked = () => resolve(false);
        }),
        STEP_TIMEOUT_MS,
        false
    );
}

/**
 * Delete the leftovers of past migrations. Returns what went, for the
 * log. Never throws: a device that cannot tidy up must still run.
 *
 * `hasUnsentWork` is asked once, before anything is deleted — with work still
 * on the device this sweep stays away entirely, however empty the old stores
 * look.
 */
export async function pruneOutdatedStores(
    hasUnsentWork: () => Promise<boolean>,
    /** The name of the database this tab has open (`db.name`). */
    databaseName: string,
    liveCheckpoints?: () => Promise<ReadonlySet<string>>
): Promise<string[]> {
    if (typeof indexedDB === 'undefined' || !indexedDB.databases) {
        return [];
    }
    try {
        if (await hasUnsentWork()) {
            return [];
        }
        const listed = await withTimeout(
            indexedDB.databases(),
            STEP_TIMEOUT_MS,
            []
        );
        const names = listed.map((entry) => entry.name ?? '');
        const checkpoints = liveCheckpoints
            ? await liveCheckpoints().catch(() => undefined)
            : undefined;
        const candidates = outdatedStoreNames(
            names,
            currentVersions(),
            databaseName,
            checkpoints
        );
        const removed: string[] = [];
        for (const name of candidates) {
            // An old collection store: provably empty, or it stays — an
            // interrupted migration still has its documents here, and RxDB
            // will come back for them. Bookkeeping holds no records.
            const rest = RAW_NAME.exec(name)?.groups?.rest ?? '';
            if (!HOUSEKEEPING.test(rest) && (await countDocs(name)) !== 0) {
                continue;
            }
            if (await deleteDatabase(name)) {
                removed.push(name);
            }
        }
        return removed;
    } catch {
        return [];
    }
}
